<!--
  @description: 消息模版管理组件
  @author: system
  @date: 2026-06-02
  @note: 负责消息模版的增删改查和配置管理
-->
<template>
  <div class="template-manage-container">
    <!-- 筛选查询框 -->
    <div class="filter-container">
      <el-form :inline="true">
        <el-button type="primary" size="small" icon="el-icon-plus" @click="handleMessageAdd">新增消息模版</el-button>
        <el-form-item label="模版名称:" style="margin:0 10px" size="small">
          <el-input
            v-model="messageQuery.template_name"
            placeholder="请输入模版名称"
            clearable
            style="width: 200px;"
            @keyup.enter.native="getMessageList"
          ></el-input>
        </el-form-item>
        <el-form-item label="公众号:" style="margin:0 10px" size="small">
          <el-select
            v-model="messageQuery.appid"
            placeholder="请选择公众号"
            clearable
            filterable
            style="width: 200px;"
          >
            <el-option
              v-for="item in gzhList"
              :key="item.appid"
              :label="item.account_name"
              :value="item.appid"
            ></el-option>
          </el-select>
        </el-form-item>
        <el-form-item label="模版CODE:" style="margin:0 10px" size="small">
          <el-input
            v-model="messageQuery.template_code"
            placeholder="请输入模版CODE"
            clearable
            style="width: 200px;"
            @keyup.enter.native="getMessageList"
          ></el-input>
        </el-form-item>
        <el-button type="primary" @click="getMessageList" size="small" icon="el-icon-search">搜索</el-button>
      </el-form>
    </div>

    <!-- 表格列表 -->
    <div class="table-container">
      <ElTable
        :columns="listHeader"
        :tableData="messageList"
        :loading="messageLoading"
        :total="messageQuery.total"
        @handleCurrentChange="handleMessageCurrentChange"
        @handleSizeChange="handleMessageSizeChange"
      >
        <!-- 公众号显示 -->
        <template slot="account_name" slot-scope="{ row }">
          <el-tag type="success" effect="dark" size="mini">{{ row.account_name || '-' }}</el-tag>
        </template>

        <!-- 模版CODE -->
        <template slot="template_code" slot-scope="{ row }">
          <span class="code-text">{{ row.template_code }}</span>
        </template>

        <!-- 操作按钮 -->
        <template slot="operation" slot-scope="{ row }">
          <div class="operation">
            <el-button size="mini" type="text" @click="handleMessageCopy(row)">克隆</el-button>
            <el-button size="mini" type="text" @click="handleMessageEdit(row)">编辑</el-button>
            <el-button
              size="mini"
              type="text"
              style="color: #F56C6C;"
              @click="handleMessageDelete(row)"
            >删除</el-button>
          </div>
        </template>
      </ElTable>
    </div>

    <!-- 消息模版抽屉 -->
    <common-drawer
      :visible.sync="messageDrawerVisible"
      :title="messageDrawerTitle"
      size="700px"
      @confirm="saveMessage"
      @cancel="handleMessageCancel"
    >
      <el-form ref="messageForm" :model="messageForm" label-width="150px" class="drawer-form">
        <el-form-item label="模版名称" required>
          <el-input v-model="messageForm.template_name" clearable placeholder="请输入模版名称"></el-input>
        </el-form-item>
        <el-form-item label="公众号名称" required>
          <el-select
            v-model="messageForm.appid"
            placeholder="请选择"
            clearable
            filterable
            style="width:100%"
          >
            <el-option
              v-for="item in gzhList"
              :key="item.appid"
              :label="item.account_name"
              :value="item.appid"
            ></el-option>
          </el-select>
        </el-form-item>
        <el-form-item label="模版code" required>
          <el-input v-model="messageForm.template_code" clearable placeholder="此处为公众号复制过来的模版code"></el-input>
        </el-form-item>
        <el-form-item label="模版内容" required>
          <div
            v-for="(item, index) in messageForm.template_msg_list"
            :key="index"
            style="display: flex; margin-bottom: 10px;"
          >
            <el-input
              size="small"
              v-model="item.keyName"
              placeholder="字段中文描述"
              style="width: 150px; margin-right: 10px;"
            ></el-input>
            <el-input
              size="small"
              v-model="item.key"
              placeholder="字段英文命名"
              style="width: 150px; margin-right: 10px;"
            ></el-input>
            <el-input
              size="small"
              v-model="item.value"
              placeholder="字段默认值"
              style="flex: 1; margin-right: 10px;"
              type="textarea"
              :rows="1"
            ></el-input>
            <el-button
              type="danger"
              size="small"
              icon="el-icon-delete"
              @click="handleDeleteItem(index)"
            ></el-button>
          </div>
          <el-button type="primary" size="small" icon="el-icon-plus" @click="handleAddItem">添加字段</el-button>
        </el-form-item>
        <el-form-item label="跳转到url">
          <el-input v-model="messageForm.url" clearable placeholder="选填"></el-input>
        </el-form-item>
        <el-form-item label="跳转小程序appid">
          <el-input v-model="messageForm.xcx_appid" clearable placeholder="此小程序需要在公众号后台设置关联"></el-input>
        </el-form-item>
        <el-form-item label="跳转到小程序路径">
          <el-input v-model="messageForm.pagepath" clearable placeholder="页面路径如：/pages/index/index"></el-input>
        </el-form-item>
      </el-form>
    </common-drawer>
  </div>
</template>

<script>
/**
 * @description 消息模版管理组件
 * @component TemplateManage
 * @requires services/sendService/sendTemplate - 消息模版相关API
 */
import {
  selectApi,
  updateApi,
  deleteApi,
  addApi
} from "@/services/sendService/sendTemplate";
import { selectApi as selectGzhListApi } from "@/services/sendService/sendAccount";
import ElTable from "@/components/elTable/index.vue";
import CommonDrawer from "@/components/drawer/index.vue";
import listHeader from "./listHeader";
import { mapState } from "vuex";

export default {
  name: "TemplateManage",
  components: {
    ElTable,
    CommonDrawer
  },
  created() {
    // 组件创建时获取公众号列表和消息模版列表
    this.getGzhList();
    this.getMessageList();
  },
  data() {
    return {
      // 消息模版列表加载状态
      messageLoading: false,
      // 消息模版查询参数
      messageQuery: {
        pageNumber: 1,
        pageSize: 10,
        total: 0,
        template_name: "",
        appid: "",
        template_code: ""
      },
      // 消息模版列表数据
      messageList: [],
      // 抽屉可见性
      messageDrawerVisible: false,
      // 抽屉标题（新增/编辑）
      messageDrawerTitle: "",
      // 表单数据
      messageForm: {
        template_msg_list: []
      },
      // 公众号列表
      gzhList: []
    };
  },
  computed: {
    listHeader,
    ...mapState(["userInfo"])
  },
  methods: {
    /**
     * @description 获取消息模版列表
     */
    async getMessageList() {
      this.messageLoading = true;
      const { data, code, count } = await selectApi(this.messageQuery);
      if (code === 200) {
        this.messageList = data;
        this.messageQuery.total = count;
        setTimeout(() => {
          this.messageLoading = false;
        }, 300);
      }
    },

    /**
     * @description 页码改变事件
     * @param {number} page - 新的页码
     */
    handleMessageCurrentChange(page) {
      this.messageQuery.pageNumber = page;
      this.getMessageList();
    },

    /**
     * @description 每页条数改变事件
     * @param {number} size - 新的每页条数
     */
    handleMessageSizeChange(size) {
      this.messageQuery.pageSize = size;
      this.getMessageList();
    },

    /**
     * @description 新增消息模版
     */
    handleMessageAdd() {
      this.messageDrawerVisible = true;
      this.messageDrawerTitle = "新增消息模版";
      this.messageForm = {
        template_msg_list: []
      };
      this.$nextTick(() => {
        this.$refs.messageForm && this.$refs.messageForm.clearValidate();
      });
    },

    /**
     * @description 编辑消息模版
     * @param {Object} row - 消息模版数据
     */
    handleMessageEdit(row) {
      this.messageDrawerVisible = true;
      this.messageDrawerTitle = "编辑消息模版";
      this.messageForm = JSON.parse(JSON.stringify(row));
      // 解析模版字段列表
      try {
        this.messageForm.template_msg_list =
          JSON.parse(this.messageForm.template_msg_list) || [];
      } catch (error) {
        this.messageForm.template_msg_list = [];
      }
      this.$nextTick(() => {
        this.$refs.messageForm && this.$refs.messageForm.clearValidate();
      });
    },

    /**
     * @description 克隆消息模版
     * @param {Object} row - 消息模版数据
     */
    handleMessageCopy(row) {
      this.$confirm(`是否确定克隆模版'${row.template_name}'？`, "提示", {
        confirmButtonText: "确定",
        cancelButtonText: "取消",
        type: "warning"
      })
        .then(() => {
          const copyData = { ...row };
          delete copyData.id;
          addApi(copyData).then(res => {
            if (res.code === 200) {
              this.$message.success("克隆成功!");
              this.getMessageList();
            }
          });
        })
        .catch(() => {});
    },

    /**
     * @description 删除消息模版
     * @param {Object} row - 消息模版数据
     */
    handleMessageDelete(row) {
      this.$confirm(
        `此操作将永久删除'${row.template_name}'，是否继续?`,
        "提示",
        {
          confirmButtonText: "确定",
          cancelButtonText: "取消",
          type: "warning"
        }
      )
        .then(() => {
          deleteApi({ id: row.id }).then(res => {
            if (res.code === 200) {
              this.$message.success("删除成功!");
              this.getMessageList();
            }
          });
        })
        .catch(() => {});
    },

    /**
     * @description 添加模版字段
     */
    handleAddItem() {
      this.messageForm.template_msg_list.push({
        keyName: "",
        key: "",
        value: ""
      });
    },

    /**
     * @description 删除模版字段
     * @param {number} index - 字段索引
     */
    handleDeleteItem(index) {
      this.messageForm.template_msg_list.splice(index, 1);
    },

    /**
     * @description 将模版字段数组转换为对象格式
     * @param {Array} array - 字段数组
     * @returns {Object} 转换后的对象
     */
    transformArrayToObject(array) {
      let transformed = {};
      for (let i = 0; i < array.length; i++) {
        const item = array[i];
        const { key, value } = item;
        transformed[key] = { value };
      }
      return transformed;
    },

    /**
     * @description 保存消息模版（新增或编辑）
     */
    saveMessage() {
      let formData = JSON.parse(JSON.stringify(this.messageForm));

      // 字段校验
      if (formData.template_msg_list.length === 0) {
        this.$message.error("请添加模版字段");
        return;
      }
      for (let i = 0; i < formData.template_msg_list.length; i++) {
        const item = formData.template_msg_list[i];
        if (!item.keyName || !item.key || !item.value) {
          this.$message.error("请完善模版字段");
          return;
        }
      }

      // 转换模版字段格式
      try {
        formData.template_msg = JSON.stringify(
          this.transformArrayToObject(formData.template_msg_list)
        );
      } catch (error) {
        formData.template_msg = null;
      }

      try {
        formData.template_msg_list = JSON.stringify(formData.template_msg_list);
      } catch (error) {
        formData.template_msg_list = null;
      }

      // 必填字段校验
      if (
        !formData.appid ||
        !formData.template_code ||
        !formData.template_name ||
        !formData.template_msg
      ) {
        this.$message.error("请填写必填字段");
        return;
      }

      if (this.messageDrawerTitle === "新增消息模版") {
        // 新增消息模版
        addApi(formData).then(res => {
          if (res.code === 200) {
            this.$message.success("新增成功!");
            this.messageDrawerVisible = false;
            this.getMessageList();
          }
        });
      } else {
        // 编辑消息模版
        updateApi(formData).then(res => {
          if (res.code === 200) {
            this.$message.success("修改成功!");
            this.messageDrawerVisible = false;
            this.getMessageList();
          }
        });
      }
    },

    /**
     * @description 取消操作
     */
    handleMessageCancel() {
      this.messageDrawerVisible = false;
    },

    /**
     * @description 获取公众号列表
     */
    async getGzhList() {
      const { data, code } = await selectGzhListApi({
        pageNumber: 1,
        pageSize: 100
      });
      if (code === 200) {
        this.gzhList = data;
      }
    },

    /**
     * @description 根据ID获取公众号名称
     * @param {number} id - 公众号ID
     * @returns {string} 公众号名称
     */
    getGzhName(id) {
      const item = this.gzhList.find(g => g.id === id);
      return item ? item.account_name : "-";
    }
  }
};
</script>

<style scoped lang="less">
.template-manage-container {
  padding: 20px 0;
  min-height: calc(100vh - 150px);

  // 筛选查询框
  .filter-container {
    background: #fff;
    padding: 20px;
    border-radius: 8px;
    box-shadow: 0 2px 12px rgba(0, 0, 0, 0.05);
    margin-bottom: 20px;

    /deep/ .el-form {
      display: flex;
      flex-wrap: wrap;
      align-items: center;
      gap: 10px;

      .el-form-item {
        margin-top: 10px !important;
        margin-bottom: 10px !important;
      }

      .el-button {
        margin-left: 10px;

        &.el-button--primary {
          background-color: #165DFF;
          border-color: #165DFF;

          &:hover,
          &:focus {
            background-color: #4080FF;
            border-color: #4080FF;
          }
        }
      }
    }
  }

  // 表格容器
  .table-container {
    background: #fff;
    padding: 0 0 20px;
    border-radius: 8px;
    box-shadow: 0 2px 12px rgba(0, 0, 0, 0.05);
    min-height: calc(100vh - 350px);

    /deep/ .el-table__empty-block {
      display: flex;
      align-items: center;
      justify-content: center;
      min-height: 400px;
      width: 100% !important;
      
      .el-table__empty-text {
        text-align: center;
        line-height: 1.6;
      }
    }

    .code-text {
      font-family: "Courier New", monospace;
      font-size: 13px;
      color: #666;
    }

    .operation {
      display: flex;
      gap: 8px;
      align-items: center;

      .el-button {
        padding: 0 8px;
      }
    }
  }

  // 抽屉表单
  .drawer-form {
    padding: 20px 0;
  }
}

// 响应式设计
@media (max-width: 768px) {
  .template-manage-container {
    .filter-container {
      /deep/ .el-form {
        flex-direction: column;
        align-items: stretch;

        .el-form-item {
          width: 100%;

          .el-input,
          .el-select {
            width: 100% !important;
          }
        }

        .el-button {
          width: 100%;
          margin-left: 0;
        }
      }
    }
  }
}
</style>
